import { useMemo, useState } from 'react'
import { RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ControlBar, ControlBarGroup } from '@/components/ui/ControlBar'
import { Select } from '@/components/ui/Select'
import { PacksTable, TransactionsList } from '@/components/revenue'
import { PageHeader, StatCard } from '@/components/shared'
import { count, money } from '@/lib'
import { CHANNELS, MONTHS, PACKS, TRANSACTIONS } from '@/fixtures'
import type { Stat } from '@/models'

export function RevenuePage() {
  const [month, setMonth] = useState(MONTHS[0])
  const [channel, setChannel] = useState(CHANNELS[0])
  const [refreshing, setRefreshing] = useState(false)

  const visible = useMemo(
    () =>
      channel === 'All channels'
        ? TRANSACTIONS
        : TRANSACTIONS.filter((transaction) => transaction.channel === channel),
    [channel],
  )

  const handleRefresh = () => {
    setRefreshing(true)
    setTimeout(() => setRefreshing(false), 600)
  }

  /* Derived from the rows actually on screen, so the tiles never disagree with
   * the list under them when the channel filter changes. */
  const stats: Stat[] = useMemo(() => {
    const gross = PACKS.reduce((sum, pack) => sum + pack.revenue, 0)
    const units = PACKS.reduce((sum, pack) => sum + pack.units, 0)
    return [
      { id: 'gross', label: 'Gross revenue', value: money(gross, true), delta: '+6.4%', up: true },
      { id: 'units', label: 'Units sold', value: count(units), delta: '+2.9%', up: true },
      { id: 'arppu', label: 'ARPPU', value: money(gross / units), delta: '−1.2%', up: false },
      {
        id: 'txn',
        label: 'Transactions shown',
        value: count(visible.length),
        delta: '+4.1%',
        up: true,
      },
    ]
  }, [visible.length])

  return (
    <div className="space-y-6">
      <PageHeader
        title="IAP Revenue"
        description="In-app purchase performance by pack and channel."
      />

      {/* Stats first, then the controls that scope them — the same order every
          page with a filter bar uses. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.id} stat={stat} />
        ))}
      </div>

      <ControlBar>
        <ControlBarGroup>
          <Select
            aria-label="Month"
            options={MONTHS.map((value) => ({ value, label: value }))}
            value={month}
            onValueChange={setMonth}
            triggerClassName="min-w-44 bg-bg-elev"
          />
          <Select
            aria-label="Channel"
            options={CHANNELS.map((value) => ({ value, label: value }))}
            value={channel}
            onValueChange={setChannel}
            triggerClassName="min-w-44 bg-bg-elev"
          />
        </ControlBarGroup>

        <Button
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          aria-label={refreshing ? 'Refreshing revenue data…' : 'Refresh revenue data'}
          className="hover:bg-bg-elev rounded-lg"
        >
          <RotateCw aria-hidden className={refreshing ? 'animate-spin' : undefined} />
        </Button>
      </ControlBar>

      {/* 50 / 50 Split Layout for Packs Table and Recent Transactions */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <PacksTable packs={PACKS} />
        <TransactionsList transactions={visible} />
      </div>
    </div>
  )
}
